import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex api
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { IEmployeeDocument } from '@/@types/company';

export function useEditEmployeeForm(employeeId: Id<'companyEmployees'> | undefined) {
  const navigate = useNavigate();
  const updateEmployee = useMutation(api.companies.employees.update);

  const employee = useQuery(api.companies.employees.get, employeeId ? { employeeId } : 'skip');

  // Basic info
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [position, setPosition] = useState('');

  // Personal info
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [identityDocumentNumber, setIdentityDocumentNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  // Documents
  const [identityDocFront, setIdentityDocFront] = useState<IEmployeeDocument | undefined>(
    undefined
  );
  const [identityDocBack, setIdentityDocBack] = useState<IEmployeeDocument | undefined>(undefined);
  const [drivingLicenseFront, setDrivingLicenseFront] = useState<IEmployeeDocument | undefined>(
    undefined
  );
  const [drivingLicenseBack, setDrivingLicenseBack] = useState<IEmployeeDocument | undefined>(
    undefined
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Pre-fill form when employee loads
  useEffect(() => {
    if (employee && !isInitialized) {
      setName(employee.name);
      setEmail(employee.email ?? '');
      setPhoneNumber(employee.phoneNumber ?? '');
      setPosition(employee.position ?? '');
      setPostalCode(employee.postalCode ?? '');
      setCity(employee.city ?? '');
      setIdentityDocumentNumber(employee.identityDocumentNumber ?? '');
      setNationality(employee.nationality ?? '');
      setDateOfBirth(employee.dateOfBirth ?? '');
      setIdentityDocFront(employee.identityDocFront);
      setIdentityDocBack(employee.identityDocBack);
      setDrivingLicenseFront(employee.drivingLicenseFront);
      setDrivingLicenseBack(employee.drivingLicenseBack);
      setIsInitialized(true);
    }
  }, [employee, isInitialized]);

  const handleDocumentChange = useCallback(
    (
      field: 'identityDocFront' | 'identityDocBack' | 'drivingLicenseFront' | 'drivingLicenseBack',
      doc: IEmployeeDocument | null
    ) => {
      const value = doc ?? undefined;
      if (field === 'identityDocFront') setIdentityDocFront(value);
      else if (field === 'identityDocBack') setIdentityDocBack(value);
      else if (field === 'drivingLicenseFront') setDrivingLicenseFront(value);
      else if (field === 'drivingLicenseBack') setDrivingLicenseBack(value);
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!employeeId) return;
      if (!name.trim()) {
        toast.error('Name is required');
        return;
      }
      setIsSubmitting(true);
      try {
        await updateEmployee({
          employeeId,
          name: name.trim(),
          email: email.trim() || undefined,
          phoneNumber: phoneNumber.trim() || undefined,
          position: position.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          city: city.trim() || undefined,
          identityDocumentNumber: identityDocumentNumber.trim() || undefined,
          nationality: nationality.trim() || undefined,
          dateOfBirth: dateOfBirth || undefined,
          identityDocFront,
          identityDocBack,
          drivingLicenseFront,
          drivingLicenseBack,
        });
        toast.success('Employee updated');
        navigate(`/company/employees/${employeeId}`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update employee');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      updateEmployee,
      navigate,
      employeeId,
      name,
      email,
      phoneNumber,
      position,
      postalCode,
      city,
      identityDocumentNumber,
      nationality,
      dateOfBirth,
      identityDocFront,
      identityDocBack,
      drivingLicenseFront,
      drivingLicenseBack,
    ]
  );

  return {
    employee,
    // Field values
    name,
    email,
    phoneNumber,
    position,
    postalCode,
    city,
    identityDocumentNumber,
    nationality,
    dateOfBirth,
    identityDocFront,
    identityDocBack,
    drivingLicenseFront,
    drivingLicenseBack,
    isSubmitting,
    // Setters
    setName,
    setEmail,
    setPhoneNumber,
    setPosition,
    setPostalCode,
    setCity,
    setIdentityDocumentNumber,
    setNationality,
    setDateOfBirth,
    // Handlers
    handleDocumentChange,
    handleSubmit,
  };
}
